import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import { login as apiLogin, logout as apiLogout, refreshToken as apiRefreshToken, tokenStorage, getMyPermissions, type LoginRequest, type ApiError } from "~/lib/api";
import { decodeToken, isTokenExpired, type TokenPayload } from "~/lib/jwt";

interface User {
  userId: string;
  role: string;
  fname: string;
  lname: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  isPermissionsLoading: boolean;
  user: User | null;
  permissions: Set<string>;
  hasPermission: (permissionName: string) => boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
  error: string | null;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isPermissionsLoading, setIsPermissionsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [permissions, setPermissions] = useState<Set<string>>(new Set());

  // Helper function to extract user from token
  const extractUserFromToken = (): User | null => {
    const accessToken = tokenStorage.getAccessToken();
    if (!accessToken) {
      return null;
    }

    const payload = decodeToken(accessToken);
    if (!payload) {
      return null;
    }

    return {
      userId: payload.userId,
      role: payload.role,
      fname: payload.fname,
      lname: payload.lname,
    };
  };

  // Check authentication status on mount and set up token refresh
  useEffect(() => {
    const checkAuth = async () => {
      const accessToken = tokenStorage.getAccessToken();
      const refreshTokenValue = tokenStorage.getRefreshToken();

      if (!accessToken || !refreshTokenValue) {
        setIsAuthenticated(false);
        setUser(null);
        setPermissions(new Set());
        setIsLoading(false);
        return;
      }

      // Check if access token is expired
      if (isTokenExpired(accessToken)) {
        // Try to refresh the token
        try {
          setIsLoading(true);
          const response = await apiRefreshToken();
          
          if (response.success && response.data) {
            // Token refreshed successfully
            const userData = extractUserFromToken();
            setIsAuthenticated(true);
            setUser(userData);
          } else {
            // Refresh failed, user needs to login again
            setIsAuthenticated(false);
            setUser(null);
            setPermissions(new Set());
          }
        } catch (error) {
          // Refresh failed, user needs to login again
          setIsAuthenticated(false);
          setUser(null);
          setPermissions(new Set());
        }
      } else {
        // Token is still valid
        const authenticated = tokenStorage.isAuthenticated();
        setIsAuthenticated(authenticated);
        
        if (authenticated) {
          const userData = extractUserFromToken();
          setUser(userData);
        } else {
          setUser(null);
          setPermissions(new Set());
        }
      }
      
      setIsLoading(false);
    };

    checkAuth();

    // Set up periodic token refresh check (every 5 minutes)
    const refreshInterval = setInterval(async () => {
      const accessToken = tokenStorage.getAccessToken();
      if (accessToken && isTokenExpired(accessToken)) {
        try {
          await apiRefreshToken();
          const userData = extractUserFromToken();
          setUser(userData);
        } catch (error) {
          // Refresh failed, logout user
          apiLogout();
          setIsAuthenticated(false);
          setUser(null);
          setPermissions(new Set());
        }
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(refreshInterval);
  }, []);

  // Load effective permission names when authenticated/user changes
  useEffect(() => {
    if (!isAuthenticated || !user) {
      setPermissions(new Set());
      return;
    }

    let cancelled = false;
    const loadPermissions = async () => {
      setIsPermissionsLoading(true);
      try {
        if (user.role === "super-admin") {
          if (!cancelled) setPermissions(new Set(["*"]));
          return;
        }
        const res = await getMyPermissions();
        if (!cancelled) setPermissions(new Set(res.data?.permissions ?? []));
      } catch {
        if (!cancelled) setPermissions(new Set());
      } finally {
        if (!cancelled) setIsPermissionsLoading(false);
      }
    };

    loadPermissions();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user]);

  const hasPermission = (permissionName: string) => {
    if (!isAuthenticated) return false;
    if (user?.role === "super-admin") return true;
    if (permissions.has("*")) return true;
    return permissions.has(permissionName);
  };

  const login = async (credentials: LoginRequest) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiLogin(credentials);

      if (response.success && response.data) {
        setIsAuthenticated(true);
        const userData = extractUserFromToken();
        setUser(userData);
        setIsLoading(false);
        // Navigation will be handled by the component using the context
      } else {
        setError(response.error || "Login failed. Please try again.");
        setIsLoading(false);
        throw new Error(response.error || "Login failed");
      }
    } catch (err) {
      const apiError = err as ApiError;
      const errorMessage =
        apiError.details?.suggestion ||
        apiError.error ||
        "An error occurred. Please try again.";
      setError(errorMessage);
      setIsLoading(false);
      setIsAuthenticated(false);
      setUser(null);
      throw err;
    }
  };

  const logout = () => {
    apiLogout();
    setIsAuthenticated(false);
    setUser(null);
    setPermissions(new Set());
    setError(null);
    // Navigation will be handled by the component using the context
  };

  const clearError = () => {
    setError(null);
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        isPermissionsLoading,
        user,
        permissions,
        hasPermission,
        login,
        logout,
        error,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

